import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Payment states
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGstr1Paid, setIsGstr1Paid] = useState(false);
  const [isGstr3bPaid, setIsGstr3bPaid] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('taxsetu_token');
    const storedUser = localStorage.getItem('taxsetu_user');
    const storedIsAdmin = localStorage.getItem('taxsetu_isAdmin');
    const storedGstr1Paid = localStorage.getItem('taxsetu_gstr1_paid');
    const storedGstr3bPaid = localStorage.getItem('taxsetu_gstr3b_paid');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsLoggedIn(true);
    }
    
    // Restore payment states
    if (storedIsAdmin === 'true') setIsAdmin(true);
    if (storedGstr1Paid === 'true') setIsGstr1Paid(true);
    if (storedGstr3bPaid === 'true') setIsGstr3bPaid(true);
    
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      // Master Admin Bypass - admin/practiq unlocks everything
      if (username === 'admin' && password === 'practiq') {
        const adminUser = { 
          username: 'admin', 
          gstin: '07AAACG1234A1ZM',
          isAdmin: true 
        };
        
        localStorage.setItem('taxsetu_token', 'admin_token');
        localStorage.setItem('taxsetu_user', JSON.stringify(adminUser));
        localStorage.setItem('taxsetu_isAdmin', 'true');
        localStorage.setItem('taxsetu_gstr1_paid', 'true');
        localStorage.setItem('taxsetu_gstr3b_paid', 'true');
        
        setToken('admin_token');
        setUser(adminUser);
        setIsLoggedIn(true);
        setIsAdmin(true);
        setIsGstr1Paid(true);
        setIsGstr3bPaid(true);
        
        return { success: true, isAdmin: true };
      }

      // Regular API login
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'Invalid credentials' };
      }

      const data = await response.json();
      
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      
      setToken(data.access_token);
      setUser(data.user);
      setIsLoggedIn(true);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Connection error. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('taxsetu_token');
    localStorage.removeItem('taxsetu_user');
    localStorage.removeItem('taxsetu_isAdmin');
    localStorage.removeItem('taxsetu_gstr1_paid');
    localStorage.removeItem('taxsetu_gstr3b_paid');
    
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setIsAdmin(false);
    setIsGstr1Paid(false);
    setIsGstr3bPaid(false);
  };

  // Function to unlock modules after payment
  const unlockModule = (moduleType) => {
    if (moduleType === 'gstr1' || moduleType === 'gst_complete') {
      setIsGstr1Paid(true);
      localStorage.setItem('taxsetu_gstr1_paid', 'true');
    }
    if (moduleType === 'gstr3b' || moduleType === 'gst_complete') {
      setIsGstr3bPaid(true);
      localStorage.setItem('taxsetu_gstr3b_paid', 'true');
    }
  };

  const getAuthHeaders = () => {
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  return (
    <AuthContext.Provider value={{ 
      isLoggedIn, 
      user, 
      token,
      loading,
      isAdmin,
      isGstr1Paid,
      isGstr3bPaid,
      login, 
      logout,
      unlockModule,
      getAuthHeaders 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
